import React, {
  memo, useEffect, useMemo, useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import Checkbox from 'components/core/Checkbox';
import Input, { states as inputStates } from 'components/core/Input';
import Select, { models as selectModels } from 'components/core/Select';

import { Container } from './styles';

function EmailSelector({
  callback, options, feedbackMessage, value, proxyEmailAdmin, readonly, setEmailExists,
}) {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [inputValue, setInputValue] = useState({ value: '', proxyEmailAdmin: null });
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);

  const optionsState = useMemo(() => {
    const mapped = options.map((opt) => ({
      ...opt,
      label: `${opt.fullName} (${opt.username})`,
      value: opt.value,
    }));

    const hasReferencedProxy = proxyEmailAdmin
      && !mapped.some((opt) => opt.username === proxyEmailAdmin);

    if (hasReferencedProxy) {
      mapped.unshift({
        label: value ? `${value} (${proxyEmailAdmin})` : proxyEmailAdmin,
        value: value || '',
        username: proxyEmailAdmin,
        fullName: value || proxyEmailAdmin,
        isDisabled: true,
      });
    }

    return mapped;
  }, [options, proxyEmailAdmin, value]);

  const selectedProxyOption = useMemo(
    () => optionsState.find((option) => option?.username === proxyEmailAdmin),
    [optionsState, proxyEmailAdmin],
  );

  useEffect(() => {
    if (proxyEmailAdmin) {
      setIsChecked(true);
      setInputValue((prev) => ({ ...prev, proxyEmailAdmin }));
    }
  }, [proxyEmailAdmin]);

  useEffect(() => {
    if (value) {
      setInputValue((prev) => ({ ...prev, value }));
    }
  }, [value]);

  useEffect(() => {
    callback(inputValue);
  }, [callback, inputValue]);

  useEffect(() => {
    if (!isChecked) {
      setInputValue({ proxyEmailAdmin: null, value: '' });
    }
  }, [isChecked]);

  const checkIfEmailExists = (email) => {
    if (!email) return;
    const exists = options.some((item) => item.value === email);
    setEmailExists(exists);
    setEmailAlreadyExists(exists);
  };

  const inputState = readonly ? inputStates.disabled : inputStates.default;

  return (
    <Container>
      {
        isChecked ? (
          <Select
            label={t('text.typeAnEmail')}
            placeholder={t('text.typeAnEmail')}
            textAlign="left"
            model={selectModels.light}
            options={optionsState}
            onChange={(eventValue) => {
              setInputValue({ proxyEmailAdmin: eventValue.username, value: eventValue.value });
            }}
            feedbackMessage={feedbackMessage}
            required
            readonly={readonly}
            value={selectedProxyOption}
          />
        ) : (
          <Input
            value={inputValue.value}
            onChange={(event) => {
              setInputValue({ proxyEmailAdmin: null, value: event.target.value });
              setEmailAlreadyExists(false);
            }}
            type="email"
            label={t('email')}
            placeholder={t('text.typeAnEmail')}
            textAlign="left"
            feedbackMessage={emailAlreadyExists && !isChecked ? t('feedback.emailAlreadyUsed', { email: inputValue.value }) : feedbackMessage}
            onBlur={() => checkIfEmailExists(inputValue.value)}
            required
            readonly={readonly}
            state={inputState}
            cy="emailProfessional"
          />
        )
      }
      <Checkbox
        readOnly={readonly}
        checked={isChecked}
        isControlled
        text={t('text.doNotHaveEmail')}
        margin="26px 0 0 0"
        onChange={(event) => {
          setIsChecked(event.target.checked);
          setEmailExists(false);
          setEmailAlreadyExists(false);
        }}
        cy="noEmail"
      />
    </Container>
  );
}

EmailSelector.defaultProps = {
  callback: () => { /* */ },
  feedbackMessage: '',
  options: [],
};

export default memo(EmailSelector);
